import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { Organisation } from 'src/app/global/global.model';
import { ConfigAllService } from 'src/app/global/services/config-all/config-all.abstract.service';
import { PatchOrganisationConfiguration } from 'src/app/global/services/config-all/config-all.model';
import { BcitiContextService } from 'src/app/global/services/context/context.abstract.service';

@Component({
    selector: 'app-config-residence',
    templateUrl: './config-residence.component.html',
    styleUrls: ['./config-residence.component.scss'],
})
export class ConfigResidenceComponent implements OnInit {
    destroy$ = new Subject();

    loading = false;
    processingSubmit = false;
    orgConfig: any;
    org: Organisation;
    residenceConfigForm = new FormGroup({});
    badges = [];
    langs: string[];
    availableTextConfigs = ['approvalGuidelines', 'supportingDocuments'];

    constructor(
        public translate: TranslateService,
        private configAllService: ConfigAllService,
        @Inject('BCITI_CONTEXT_SERVICE') private contextService: BcitiContextService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit() {
        this.contextService
            .getLanguage()
            .pipe(
                takeUntil(this.destroy$),
                tap((lang) => this.translate.use(lang)),
            )
            .subscribe();

        combineLatest([this.contextService.getOrganisationObs(), this.contextService.getAvailableLanguages(), this.contextService.getOrganisationConfiguration()])
            .pipe(
                takeUntil(this.destroy$),
                filter(([org, langs, config]) => !!org && !!langs && !!config),
                map(([org, langs, config]) => {
                    this.org = org;
                    this.langs = langs;
                    langs.forEach((lang) => {
                        this.availableTextConfigs.forEach((textConfig) => {
                            this.residenceConfigForm.addControl(`${lang}-${textConfig}`, new FormControl());
                        });
                    });

                    if (!this.residenceConfigForm.contains('minAttachments')) {
                        this.residenceConfigForm.addControl('minAttachments', new FormControl(0, [Validators.required, Validators.pattern(/^\d+$/)]));
                    }

                    this.orgConfig = config;
                    this.populateForm();
                    this.cdr.detectChanges();
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    refreshOrgConfig() {
        this.contextService.refreshOrganisationConfiguration().pipe(takeUntil(this.destroy$)).subscribe();
    }

    public cancel() {
        this.populateForm();
        this.contextService.successMessage(this.translate.instant('pages.config-all.config-residence.messages.cancelled'));
    }

    public submit() {
        this.processingSubmit = true;
        const orgResidence: any = {};

        for (const config of this.availableTextConfigs) {
            orgResidence[config] = {};
            for (const lang of this.langs) {
                orgResidence[config][lang] = this.residenceConfigForm.controls[`${lang}-${config}`].value;
            }
        }

        orgResidence.minAttachments = Number(this.residenceConfigForm.controls['minAttachments'].value);

        const payload: PatchOrganisationConfiguration = {
            group: 'residence',
            data: orgResidence,
        };

        this.configAllService
            .patchOrganisationConfiguration(payload, this.org.tag)
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (response) => {
                    this.processingSubmit = false;
                    this.contextService.successMessage(this.translate.instant('pages.config-all.config-residence.messages.success'));
                    this.refreshOrgConfig();
                },
                (error) => {
                    this.processingSubmit = false;
                    this.contextService.errorMessage(this.translate.instant('pages.config-all.config-residence.messages.error'));
                    console.log(error);
                },
            );
    }

    private populateForm() {
        for (const lang of this.langs) {
            for (const config of this.availableTextConfigs) {
                const value = this.orgConfig?.residence?.[config]?.[lang] || '';
                this.residenceConfigForm.controls[`${lang}-${config}`].setValue(value);
            }
        }
        const requiredDocs = this.orgConfig?.residence?.minAttachments ?? 0;
        this.residenceConfigForm.controls['minAttachments']?.setValue(requiredDocs);
    }

    /**
     * Validate the form.
     */
    public validateForm() {
        return this.residenceConfigForm.valid;
    }
}
